import { ZeroAddress } from 'ethers'

import { Network } from '../interfaces/network'
import { STK_WALLET, WALLET_STAKING_ADDR, WALLET_TOKEN } from './addresses'

const COINGECKO_API_BASE_URL = 'https://api.coingecko.com/api/v3/coins/'
const COINGECKO_BASE_URL = 'https://www.coingecko.com/en/coins/'

/**
 * ZKX: chains whose CoinGecko *coin* feed misprices the native asset, so we
 * price it through its wrapped ERC-20 contract instead.
 *
 * PulseChain is the case that forced this: the `pulsechain` coin id is quoted
 * from a handful of tiny CEX listings (a few hundred dollars of daily volume)
 * and sits ~20-25% under the real market - measured 2026-09-03: coin id
 * $0.00000836 vs $0.00001050 on PulseX and on CoinGecko's own WPLS contract
 * feed. Every PLS balance, gas quote and fee ratio in the wallet inherited that
 * gap. The wrapped contract price comes from the same proxy and tracks the DEX.
 */
const NATIVE_PRICED_BY_WRAPPED_CHAIN_IDS = [369n]

/**
 * The ERC-20 to price a native asset through, or null to price it as a coin id.
 */
export function geckoNativeAliasAddress(address: string, network: Network): string | null {
  if (address !== ZeroAddress) return null
  if (!NATIVE_PRICED_BY_WRAPPED_CHAIN_IDS.includes(network.chainId)) return null

  return network.wrappedAddr || null
}

// @TODO some form of a constants list
export function geckoIdMapper(address: string, network: Network): string | null {
  // priced through the wrapped contract (the `:${chainId}` request segment), so
  // it must NOT resolve to a coin id here
  if (geckoNativeAliasAddress(address, network)) return null

  if (address === ZeroAddress) return network.nativeAssetId

  // citrea wrapepd cbtc
  if (network.chainId === 4114n && address === '0x3100000000000000000000000000000000000006')
    return network.nativeAssetId

  // citrea wbtc
  if (network.chainId === 4114n && address === '0xDF240DC08B0FdaD1d93b74d5048871232f6BEA3d')
    return 'wrapped-bitcoin'

  // we currently can't map aave so we're leaving this
  if (address === '0x4da27a545c0c5B758a6BA100e3a049001de870f5') return 'aave'

  return null
}

/**
 * Maps specific token addresses to alternative addresses if they are missing on
 * CoinGecko (so that they are aliased to existing tokens).
 */
export function geckoTokenAddressMapper(address: string) {
  // stkWALLET and xWALLET are missing on CoinGecko, so alias to WALLET token (which exists)
  if ([STK_WALLET, WALLET_STAKING_ADDR].includes(address)) return WALLET_TOKEN

  return address
}

/**
 * Constructs the CoinGecko API URL for a given token address and network ID.
 * Handles special cases where the CoinGecko API handles differently certain
 * tokens like the native tokens.
 */
export function getCoinGeckoTokenApiUrl({
  tokenAddr,
  geckoChainId,
  geckoNativeCoinId
}: {
  tokenAddr: string
  geckoChainId: string
  geckoNativeCoinId: string
}) {
  // CoinGecko does not handle native assets (ETH, MATIC, BNB...) via the /contract endpoint.
  // Instead, native assets are identified by URL with the `nativeAssetId` directly.
  if (tokenAddr === ZeroAddress) return `${COINGECKO_API_BASE_URL}${geckoNativeCoinId}`

  const geckoTokenAddress = geckoTokenAddressMapper(tokenAddr)
  return `${COINGECKO_API_BASE_URL}${geckoChainId}/contract/${geckoTokenAddress}`
}

/** Constructs the CoinGecko URL for a given token slug. */
export const getCoinGeckoTokenUrl = (slug: string) => `${COINGECKO_BASE_URL}${slug}`
